// ipad-pwa/src/App.jsx
// =============================================================================
// Screen state-machine + server-side state reconciliation.
//
// State flow:
//   no device paired                         → PairingScreen
//   device paired, no active session         → IdleScreen
//   user taps "Sign in"                      → SignInScreen
//   server returns 409                       → OverlapScreen → resolved → SignInScreen
//   server returns 200                       → ActiveSessionScreen
//   user taps "Sign out"                     → SignOutConfirmScreen → success → IdleScreen
//
// Visibility sync:
//   On mount AND every time the PWA becomes visible again (iPad wakes from
//   sleep, user returns to the home-screen icon, etc.), we hit
//   GET /api/sessions/active and reconcile localStorage against the server.
//   Three outcomes:
//     1. Server has same session as local → no-op, render as usual.
//     2. Server says no active but local has one → server auto-closed it
//        while we were asleep. Clear local, route to idle, show a one-time
//        banner so the next pilot understands what happened.
//     3. Server returns 401 → our device token was revoked by admin.
//        Wipe everything, force re-pairing.
// =============================================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import PairingScreen from './screens/PairingScreen.jsx';
import IdleScreen from './screens/IdleScreen.jsx';
import SignInScreen from './screens/SignInScreen.jsx';
import OverlapScreen from './screens/OverlapScreen.jsx';
import ActiveSessionScreen from './screens/ActiveSessionScreen.jsx';
import EditCathayIdScreen from './screens/EditCathayIdScreen.jsx';
import SignOutConfirmScreen from './screens/SignOutConfirmScreen.jsx';
import StaffCardCaptureScreen from './screens/StaffCardCaptureScreen.jsx';
import {
  getDevice,
  getSealedBundle,
  getActiveSession,
  setActiveSession,
  clearActiveSession,
} from './lib/storage.js';
import { bootstrap, logout, getDeviceRecord, getMode } from './lib/auth.js';
import { api } from './lib/api.js';

// On import, kick off the sealed-bundle handshake (or legacy fallback)
// before React renders. The promise resolves once `getDeviceToken()` is
// populated; the App's useEffect awaits it before the first sync.
const _bootPromise = bootstrap();

// Ask the browser to mark our localStorage as "persistent" so iOS Safari
// (and other ITP-style storage managers) don't evict it under storage
// pressure. Without this, an installed PWA can lose its device token
// after a reboot or storage cleanup, forcing a re-pair. Supported on
// Safari 15.4+. Best-effort — failures are silently ignored.
if (typeof navigator !== 'undefined' && navigator.storage?.persist) {
  navigator.storage.persist().catch(() => {});
}

function _initialDevice() {
  // Prefer the Phase-2 sealed bundle's device record, fall back to legacy.
  return getSealedBundle()?.device || getDevice()?.device || null;
}

function _initialScreen() {
  if (!getSealedBundle() && !getDevice()) return 'pairing';
  return getActiveSession() ? 'active' : 'idle';
}

export default function App() {
  const [device, setDeviceState] = useState(_initialDevice);
  const [session, setSession] = useState(() => getActiveSession());
  const [screen, setScreen] = useState(_initialScreen);
  const [booting, setBooting] = useState(true);
  const [overlapCtx, setOverlapCtx] = useState(null);
  // v2 only — CathayID typed on SignInScreen, awaiting photo on StaffCardCaptureScreen.
  const [pendingV2CathayId, setPendingV2CathayId] = useState(null);
  // { cathay_id, signin_at_utc } shown briefly on the idle screen after we
  // discover a session was auto-closed server-side while the iPad was asleep.
  const [autoCloseInfo, setAutoCloseInfo] = useState(null);

  // Suppress overlapping syncs (e.g. visibilitychange + setInterval racing).
  const syncing = useRef(false);

  const syncWithServer = useCallback(async () => {
    if (syncing.current) return;
    syncing.current = true;
    try {
      // Wait for the sealed-bundle handshake (or legacy fallback) to
      // finish populating the auth cache. Without this, the first
      // /sessions/active call would 401 because the bearer header was
      // empty.
      await _bootPromise;
      const mode = getMode();
      if (mode === 'unpaired') {
        setScreen('pairing');
        return;
      }
      // First sync after bootstrap — adopt the device record auth.js
      // resolved (sealed bundle's device, or legacy device, depending).
      const resolved = getDeviceRecord();
      if (resolved && (!device || device.id !== resolved.id)) {
        setDeviceState(resolved);
      }
      let serverActive = null;
      try {
        const r = await api.active();
        serverActive = r?.session || null;
      } catch (err) {
        // Only treat a 401 as "the device is actually revoked / deleted"
        // when the server response says so explicitly. Other 401s (e.g.
        // the request went out without an Authorization header because
        // bootstrap had a transient handshake failure) must NOT wipe the
        // pairing — that's how iPads end up demanding a fresh pairing
        // code after every restart. Anything else is treated as a
        // network blip and retried on the next visibilitychange.
        const msg = String(err?.data?.error || err?.message || '').toLowerCase();
        const isRevoked =
          err.status === 401 &&
          (msg.includes('invalid device token') || msg.includes('device is revoked'));
        if (isRevoked) {
          logout();
          clearActiveSession();
          setDeviceState(null);
          setSession(null);
          setScreen('pairing');
          return;
        }
        return;
      }
      const local = getActiveSession();

      if (serverActive && (!local || local.id !== serverActive.id)) {
        // Server has an open session our local state didn't know about.
        // Adopt it (server is the source of truth).
        setActiveSession(serverActive);
        setSession(serverActive);
        setScreen('active');
        return;
      }
      if (!serverActive && local) {
        // Server says no open session but we thought there was one. The cron
        // job auto-closed it while we were asleep. Clear and notify.
        setAutoCloseInfo({
          cathay_id: local.cathay_id_entered,
          signin_at_utc: local.signin_at_utc,
        });
        clearActiveSession();
        setSession(null);
        setScreen('idle');
        // The banner self-dismisses after 30s.
        setTimeout(() => setAutoCloseInfo(null), 30_000);
        return;
      }
      // No active session on either side — but we ARE paired. The
      // initial screen state was computed BEFORE the IDB-backed cache
      // hydrated, so if the user reload-without-active-session lands
      // here, the initial 'pairing' state needs correcting now.
      if (resolved && screen === 'pairing') {
        setScreen('idle');
      }
    } finally {
      syncing.current = false;
    }
  }, []);

  // Initial sync on mount — runs ONCE bootstrap has resolved.
  useEffect(() => {
    (async () => {
      await syncWithServer();
      setBooting(false);
    })();
  }, [syncWithServer]);

  // Sync on every visibilitychange that brings us back to foreground.
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'visible') syncWithServer();
    };
    document.addEventListener('visibilitychange', onVis);
    // 'pageshow' covers the bfcache-restore case on iOS Safari.
    window.addEventListener('pageshow', onVis);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('pageshow', onVis);
    };
  }, [syncWithServer]);

  // Wake-lock so the iPad doesn't dim during a session. iOS drops the lock
  // when the page goes hidden, so re-acquire on visibilitychange too.
  useEffect(() => {
    let wakeLock = null;
    const acquire = async () => {
      try {
        if ('wakeLock' in navigator) wakeLock = await navigator.wakeLock.request('screen');
      } catch {
        /* swallow */
      }
    };
    acquire();
    const onVis = () => {
      if (document.visibilityState === 'visible') acquire();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      try { wakeLock?.release(); } catch { /* swallow */ }
    };
  }, []);

  if (booting) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="text-kiosk text-slate-500">Loading…</div>
      </div>
    );
  }

  if (screen === 'pairing') {
    return (
      <PairingScreen
        onPaired={(d) => {
          setDeviceState(d);
          setScreen('idle');
        }}
      />
    );
  }

  if (screen === 'idle') {
    return (
      <IdleScreen
        device={device}
        onStart={() => setScreen('signin')}
        autoCloseInfo={autoCloseInfo}
        onDismissAutoClose={() => setAutoCloseInfo(null)}
      />
    );
  }

  if (screen === 'signin') {
    return (
      <SignInScreen
        device={device}
        onSignedIn={(s) => {
          setSession(s);
          setScreen('active');
        }}
        onOverlap={({ cathayId, existing }) => {
          setOverlapCtx({ cathayId, existing });
          setScreen('overlap');
        }}
        onNeedStaffCard={(cathayId) => {
          setPendingV2CathayId(cathayId);
          setScreen('staffCard');
        }}
        onCancel={() => setScreen('idle')}
      />
    );
  }

  if (screen === 'staffCard') {
    return (
      <StaffCardCaptureScreen
        device={device}
        cathayId={pendingV2CathayId}
        onSignedIn={(s) => {
          setSession(s);
          setPendingV2CathayId(null);
          setScreen('active');
        }}
        onOverlap={({ cathayId, existing }) => {
          setOverlapCtx({ cathayId, existing });
          setPendingV2CathayId(null);
          setScreen('overlap');
        }}
        onCancel={() => {
          setPendingV2CathayId(null);
          setScreen('idle');
        }}
      />
    );
  }

  if (screen === 'overlap') {
    return (
      <OverlapScreen
        device={device}
        attemptedCathayId={overlapCtx?.cathayId}
        existing={overlapCtx?.existing}
        onCancel={() => {
          setOverlapCtx(null);
          setScreen('idle');
        }}
        onResolved={() => {
          setOverlapCtx(null);
          setScreen('signin');
        }}
      />
    );
  }

  if (screen === 'active') {
    return (
      <ActiveSessionScreen
        device={device}
        session={session}
        onSignOut={() => setScreen('signout')}
        onEditCathayId={() => setScreen('editCathay')}
      />
    );
  }

  if (screen === 'editCathay') {
    return (
      <EditCathayIdScreen
        device={device}
        session={session}
        onSaved={(updated) => {
          setSession(updated);
          setScreen('active');
        }}
        onCancel={() => setScreen('active')}
      />
    );
  }

  if (screen === 'signout') {
    return (
      <SignOutConfirmScreen
        device={device}
        session={session}
        onDone={() => {
          clearActiveSession();
          setSession(null);
          setScreen('idle');
        }}
        onCancel={() => setScreen('active')}
      />
    );
  }

  return null;
}
